import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useBeforeUnload, useLocation, useNavigate } from 'react-router-dom';

const UploadGuardContext = createContext(null);

const LEAVE_UPLOAD_MESSAGE = 'You have an upload in progress. If you leave now, your reviewed information will be discarded.';

const isProtectedUploadPath = (pathname) => {
    return pathname === '/upload/step2' || pathname === '/upload/step3';
};

export const UploadGuardProvider = ({ children }) => {
    const [hasUploadInProgress, setHasUploadInProgress] = useState(false);
    const markUploadInProgress = useCallback(() => setHasUploadInProgress(true), []);
    const clearUploadInProgress = useCallback(() => setHasUploadInProgress(false), []);

    const value = useMemo(() => ({
        hasUploadInProgress,
        markUploadInProgress,
        clearUploadInProgress
    }), [hasUploadInProgress, markUploadInProgress, clearUploadInProgress]);

    return (
        <UploadGuardContext.Provider value={value}>
            {children}
            <UploadNavigationGuard />
        </UploadGuardContext.Provider>
    );
};

export const useUploadGuard = () => {
    const context = useContext(UploadGuardContext);

    if (!context) {
        throw new Error('useUploadGuard must be used inside UploadGuardProvider');
    }

    return context;
};

const UploadNavigationGuard = () => {
    const { hasUploadInProgress, clearUploadInProgress } = useUploadGuard();
    const location = useLocation();
    const navigate = useNavigate();
    const [pendingNavigation, setPendingNavigation] = useState(null);

    useBeforeUnload((event) => {
        if (!hasUploadInProgress) {
            return;
        }

        event.preventDefault();
        event.returnValue = LEAVE_UPLOAD_MESSAGE;
    });

    useEffect(() => {
        const handleClick = (event) => {
            if (
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.altKey ||
                event.ctrlKey ||
                event.shiftKey ||
                !hasUploadInProgress ||
                !isProtectedUploadPath(location.pathname)
            ) {
                return;
            }

            const link = event.target.closest('a[href]');
            if (!link || link.target === '_blank' || link.hasAttribute('download')) {
                return;
            }

            const nextUrl = new URL(link.href, window.location.origin);
            if (nextUrl.origin !== window.location.origin || isProtectedUploadPath(nextUrl.pathname)) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            setPendingNavigation(`${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
        };

        document.addEventListener('click', handleClick, true);
        return () => document.removeEventListener('click', handleClick, true);
    }, [hasUploadInProgress, location.pathname]);

    useEffect(() => {
        if (!pendingNavigation) {
            return;
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setPendingNavigation(null);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [pendingNavigation]);

    if (!pendingNavigation) {
        return null;
    }

    const handleLeave = () => {
        const destination = pendingNavigation;
        setPendingNavigation(null);
        clearUploadInProgress();
        navigate(destination);
    };

    return (
        <div className="upload-leave-modal-backdrop" role="presentation">
            <div className="upload-leave-modal" role="dialog" aria-modal="true" aria-labelledby="upload-leave-title">
                <h2 id="upload-leave-title">Leave upload?</h2>
                <p>{LEAVE_UPLOAD_MESSAGE}</p>
                <div className="upload-leave-actions">
                    <button type="button" className="secondary-btn" onClick={() => setPendingNavigation(null)}>
                        Stay
                    </button>
                    <button type="button" className="danger-btn" onClick={handleLeave}>
                        Leave page
                    </button>
                </div>
            </div>
        </div>
    );
};
